import { applyCompoundOverrides, normalizeCbSlug } from './utils/applyManualCompoundOverrides';
import { approvedLabRows, approvedLabTestedDate, hasApprovedLabResults, supplementalColoradoCannabinoidRows } from './utils/approvedLabResults';
import CoaLinkBar from './landingPage/coa/CoaLinkBar';
import { useMemo, type ReactNode } from 'react';
import {
  cannabinoidOverrideKey,
  labOverrideKeys, terpeneOverrideKey,
  CANNABINOIDS,
  TERPENES,
  CANNABINOID_LABELS,
  TERPENE_LABELS,
} from './PackageLabResultsOverrides';
import LabResultsCannabinoidsTable from './components/LabResultsCannabinoidsTable';
import { formatDate } from './utils/formatDate';
import { terpeneKeys, terpenesMeta } from '@wysiwyg/moss-runtime';
import { parseLabMeasurement, selectLabPercent, projectManualLabWeight, selectManualLabWeight, selectEditorLabWeight, compoundLabWeight } from './utils/labMeasurement';
import { manualOverrideState, manualCompoundValue, manualCompoundSource, manualCompoundField, manualCompoundFieldValue, hasManualCompoundField } from './utils/manualLabOverrides';

const _terpeneKeyLookup: Record<string, string> = {};
for (const k of terpeneKeys) _terpeneKeyLookup[k.toLowerCase()] = k;

const _terpeneAliasToCanonical: Record<string, string> = {};
const _terpenesSet = new Set<string>(TERPENES);
for (const k of terpeneKeys) {
  if (_terpenesSet.has(k)) {
    _terpeneAliasToCanonical[k] = k;
  } else {
    const meta = terpenesMeta[k];
    for (const t of TERPENES) {
      if (terpenesMeta[t] === meta) {
        _terpeneAliasToCanonical[k] = t;
        break;
      }
    }
  }
}

const toCanonicalTerpeneKey = (key: string): string => {
  const ascii = key
    .replace(/^α/, 'a')
    .replace(/^β/, 'b')
    .replace(/^γ/, 'g')
    .replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  const metaKey = _terpeneKeyLookup[ascii.toLowerCase()];
  if (metaKey) return _terpeneAliasToCanonical[metaKey] ?? metaKey;
  return ascii;
};

type CannabinoidResult = {
  weightMgG?: number;
  percent?: number;
  uom?: string;
};

type TerpeneResult = {
  weightMgG?: number;
  percent?: number;
};

type TerpenePreviewRow = {
  key: string;
  rowId?: string;
  label: string;
  percent?: number;
  resultText?: string;
};

type LabResultsLike = {
  coaUrl?: string | null;
  cultivar?: string | null;
  batch?: string | null;
  facility?: string | null;
  license?: string | null;
  testedBy?: string | null;
  testedDate?: string | null;
  laboratoryLicense?: string | null;
  labTestStatus?: string | null;
  cannabinoids?: Record<string, CannabinoidResult> | null;
  terpenes?: Record<string, TerpeneResult> | null;
  origin?: string | null;
};

const formatPercent = (value?: number) => {
  const measurement = parseLabMeasurement(value);
  if (measurement === undefined) return '';
  const fixed = measurement.toFixed(2);
  return `${fixed.replace(/\.00$/, '')}%`;
};

type PackageLabResultsPreviewProps = {
  form: any;
  manualOverrides?: Record<string, unknown>;
  showPreviewLabel?: boolean;
  wrapInCard?: boolean;
  showHeader?: boolean;
  showCoaButton?: boolean;
  showCoaFields?: boolean;
  coaButtonLabel?: string;
  coaFiles?: string[];
  showRemoveCoaButton?: (index: number) => void;
  beforeCannabinoids?: ReactNode;
  terpenesLayout?: 'single' | 'twoColumn';
};

export default function PackageLabResultsPreview({
  form,
  manualOverrides,
  showPreviewLabel = true,
  wrapInCard = true,
  showHeader = true,
  showCoaButton = true,
  showCoaFields = true,
  coaButtonLabel: _coaButtonLabel = 'View COA',
  coaFiles,
  showRemoveCoaButton: showRemoveCoaButton,
  beforeCannabinoids,
  terpenesLayout = 'single',
}: PackageLabResultsPreviewProps) {
  const rawLabPayload = (form?.labResults || form?.coa || form?.lab || null) as any;
  // Save applies explicit CB intent before a missing TP family borrows ingredients.
  // Compute once each render; inferred form controls never become authored intent.
  const rawIngredients = rawLabPayload?.ingredients || rawLabPayload?.Ingredients;
  const ingredientSource = rawIngredients && typeof rawIngredients === 'object' && !Array.isArray(rawIngredients) ? rawIngredients : {};
  const explicitIntent = manualOverrides && typeof manualOverrides === 'object' && !Array.isArray(manualOverrides) ? manualOverrides : {};
  const terpeneFallbackIngredients = applyCompoundOverrides(
    ingredientSource, explicitIntent, 'lab_cb_', normalizeCbSlug,
    CANNABINOIDS, cannabinoidOverrideKey, true, explicitIntent,
  );

  
  const approved = hasApprovedLabResults(rawLabPayload);
  const approvedCannabinoids = approvedLabRows(rawLabPayload, 'cannabinoid');
  const approvedTerpenes = approvedLabRows(rawLabPayload, 'terpene');
  // Inferred hydration values are not manual intent. Only the editor supplies
  // saved/user-authored markers; other consumers retain the raw approved view.
  const hasManualOverride = (enabled: string) => typeof manualOverrideState(manualOverrides, enabled) === 'boolean';
  const lab: LabResultsLike = useMemo(() => {
    const fromForm = rawLabPayload as any;

    // COA payloads sometimes use `ingredients` and `terpenes` keys instead of `cannabinoids`.
    const rawCannabinoids = (fromForm?.cannabinoids || fromForm?.Cannabinoids || fromForm?.ingredients || null) as Record<string, any>;
    const rawTerpenes = (fromForm?.terpenes || fromForm?.Terpenes || null) as Record<string, any>;

    const cannabinoids: Record<string, CannabinoidResult> = {};
    if (rawCannabinoids) {
      Object.entries(rawCannabinoids).forEach(([key, value]) => {
        const raw = value as any;
        const weight = raw?.weight ?? {};
        const percentValue = parseLabMeasurement(raw?.percent) ?? parseLabMeasurement(weight?.percent);
        const weightValue = parseLabMeasurement(weight?.weightMgG) ??
          parseLabMeasurement(weight?.amount) ?? parseLabMeasurement(weight?.pkg) ??
          parseLabMeasurement(weight?.srv) ?? parseLabMeasurement(weight?.ml) ??
          parseLabMeasurement(raw?.weightMgG);
        const uom = weight?.unit || raw?.uom || 'mg/g';
        cannabinoids[key] = {
          percent: percentValue,
          weightMgG: weightValue,
          uom,
        } as any;
      });
    }

    const terpenes: Record<string, TerpeneResult> = {};
    if (rawTerpenes) {
      Object.entries(rawTerpenes).forEach(([key, value]) => {
        const raw = value as any;
        const weight = raw?.weight ?? {};
        const percentValue = parseLabMeasurement(raw?.percent) ?? parseLabMeasurement(weight?.percent);
        const weightValue = parseLabMeasurement(weight?.weightMgG) ??
          parseLabMeasurement(weight?.amount) ?? parseLabMeasurement(weight?.pkg) ??
          parseLabMeasurement(weight?.srv) ?? parseLabMeasurement(weight?.ml) ??
          parseLabMeasurement(raw?.weightMgG);
        
        // Normalize the key to canonical Greek form so TERPENES_ORDER lookups match
        const canonicalKey = toCanonicalTerpeneKey(key);
        terpenes[canonicalKey] = {
          percent: percentValue,
          weightMgG: weightValue,
        } as any;
      });
    }

    // Map common COA/top-level fields into preview fields
    const coaUrl = fromForm?.coaUrl || fromForm?.url || fromForm?.labReport || null;
    // Cultivar is the strain; falling back to the product name would invent one.
    const cultivar = fromForm?.cultivar || fromForm?.strainName || fromForm?.strain || null;
    const batch = fromForm?.batch || fromForm?.Batch || fromForm?.lot || null;
    const facility = fromForm?.facility || fromForm?.manufacturer?.name || form?.facility || null;
    const license = fromForm?.license || fromForm?.manufacturer?.licenseNumber || form?.facility || null;
    const testedBy = (approved ? fromForm?.lab?.name : fromForm?.testedBy || fromForm?.lab?.name) || null;
    const laboratoryLicense = (approved ? fromForm?.lab?.licenseNumber : fromForm?.laboratoryLicense || fromForm?.lab?.licenseNumber) || null;
    let testedDate = (approved ? approvedLabTestedDate(fromForm) : fromForm?.testedDate) || null;
    try {
      if (!approved && !testedDate && Array.isArray(fromForm?.labTests) && fromForm.labTests.length > 0) {
        testedDate = fromForm.labTests[0]?.at || testedDate;
      }
    } catch (e) {
      console.log(e);
    }

    const labTestStatus = (approved ? fromForm?.labTestingStateName : fromForm?.labTestStatus || fromForm?.labTestingStateName || fromForm?.status) || null;

    return {
      coaFileIds: coaUrl,
      cultivar,
      batch,
      facility,
      license,
      testedBy,
      testedDate,
      laboratoryLicense,
      labTestStatus,
      cannabinoids: cannabinoids || {},
      terpenes: terpenes || {}
    };
  }, [form, rawLabPayload, approved]);

  const getOverride = (key: { enabled: string; value: string }) => {
    if (approved && [labOverrideKeys.testedBy.value, labOverrideKeys.testedDate.value, labOverrideKeys.laboratoryLicense.value].some(value => value === key.value)
      && manualOverrideState(manualOverrides, key.enabled) !== true) return undefined;
    if (form?.[key.enabled] === false) return '—';
    // Not set at all → fall through to COA value
    if (!form?.[key.enabled]) return undefined;
    const v = form?.[key.value];
    if (v === undefined || v === null || v === '') return '—';
    return String(v);
  };

  const getCannabinoid = (key: string) => {
    const overrideKey = cannabinoidOverrideKey(key);
    const formState = manualOverrideState(form, overrideKey.enabled);
    const savedState = manualOverrideState(manualOverrides, overrideKey.enabled);
    const overrideEnabled = formState === false || savedState === false ? false : formState ?? savedState;
    // Explicit intent owns aliased fields over inferred hydration aliases.
    const fieldValue = (suffix: string) => manualCompoundFieldValue(
      hasManualCompoundField(manualOverrides, overrideKey.enabled, suffix) ? manualOverrides : form,
      overrideKey.enabled, suffix);
    const overrideWeight = fieldValue('weightMgG');
    const overridePercent = fieldValue('percent');
    const overrideUom = fieldValue('uom');

    // If the override has been explicitly disabled, treat as removed
    if (overrideEnabled === false) {
      return { key, override: false, weight: undefined, percent: undefined };
    }

    /* If override is enabled, prefer the override values. Otherwise fall back to lab COA values.
    * 'THC' is a deprecated alias for 'delta9thc' — fall back to delta9thc lab data if thc is absent.
    */
    // Read the family before canonical rows are deduplicated. A present canonical
    // entry owns its value (including clears); retained aliases are fallback only.
    const familyData = manualCompoundValue(lab.cannabinoids, key, overrideKey.enabled) as CannabinoidResult;
    const labData = Object.keys(familyData).length ? familyData
      : key === 'thc' ? manualCompoundValue(lab.cannabinoids, 'delta9thc', cannabinoidOverrideKey('delta9thc').enabled) as CannabinoidResult : undefined;
    let weight = overrideEnabled === true ? (overrideWeight ?? labData?.weightMgG) : labData?.weightMgG;
    let percent = overrideEnabled === true ? (overridePercent ?? labData?.percent) : labData?.percent;

    let uom = (typeof overrideUom === 'string' ? overrideUom : undefined) ?? labData?.uom ?? 'mg/g';
    if (hasManualOverride(overrideKey.enabled)) {
      const compounds = rawLabPayload?.cannabinoids || rawLabPayload?.Cannabinoids || rawLabPayload?.ingredients;
      const compound = manualCompoundValue(compounds, key, overrideKey.enabled);
      const sourceWeight = compoundLabWeight(compound);
      const editedWeight = projectManualLabWeight(sourceWeight, manualOverrides, overrideKey.enabled);
      const previewWeight = hasManualCompoundField(manualOverrides, overrideKey.enabled, 'uom')
        ? { ...editedWeight, unit: uom } : editedWeight;
      const selected = selectEditorLabWeight(previewWeight, manualOverrides, overrideKey.enabled);
      percent = hasManualCompoundField(manualOverrides, overrideKey.enabled, 'percent') ? overridePercent : selectLabPercent(compound);
      weight = selected.value;
      uom = selected.uom;
    }

    return {
      key,
      override: overrideEnabled === true,
      weight: parseLabMeasurement(weight),
      percent: parseLabMeasurement(percent),
      uom,
    };
  }

  const pinnedCannabinoids = ['totalCannabinoids', 'totalActiveCannabinoids', 'totalThc', 'totalCbd'];

  // Include any cannabinoid keys the user has explicitly enabled via overrides that aren't in the static order list
  const overriddenCannabinoidKeys = Object.keys(form || {})
    .filter((k) => k.startsWith('lab_cb_') && k.endsWith('_override') && form[k] === true)
    .map((k) => {
      const family = manualCompoundField(k)?.family;
      const alias = k.slice('lab_cb_'.length, -'_override'.length);
      return [...(CANNABINOIDS as readonly string[]), 'thc'].find(key =>
        manualCompoundField(cannabinoidOverrideKey(key).enabled)?.family === family) ?? alias;
    });

  // Static canonical keys take precedence over retained aliases for one family.
  const seenCannabinoidFamilies = new Set<string>();
  const cannabinoidKeys = [...(CANNABINOIDS as readonly string[]), ...overriddenCannabinoidKeys]
    .filter(key => {
      const family = manualCompoundField(cannabinoidOverrideKey(key).enabled)?.family ?? key.toLowerCase();
      if (seenCannabinoidFamilies.has(family)) return false;
      seenCannabinoidFamilies.add(family);
      return true;
    });

  const sortedCannabinoids = [
    ...pinnedCannabinoids,
    ...cannabinoidKeys
        .filter((c) => !pinnedCannabinoids.includes(c))
        .sort((a, b) => {
      const valueA = getCannabinoid(a).percent ?? 0;
      const valueB = getCannabinoid(b).percent ?? 0;
      return valueB - valueA;
    })
  ];

  const cannabinoidDisplayMode = (approved ? 'Both' : form?.amountOptions || 'Both') as 'Both' | 'Percent' | 'Weight';

  const effectiveCannabinoidRows = sortedCannabinoids
    .map((key) => {
      const row = getCannabinoid(key);
      return {
        key: row.key,
        label: `${CANNABINOID_LABELS[row.key] || row.key}${approved && hasManualOverride(cannabinoidOverrideKey(row.key).enabled) ? ' (override)' : ''}`,
        weight: row.weight,
        percent: row.percent,
        uom: row.uom,
        override: row.override,
      };
    })
    .filter((row) => row.override || (row.weight !== undefined && row.weight !== null) || (row.percent !== undefined && row.percent !== null));
  const cannabinoidRows = approved ? [
    ...approvedCannabinoids.filter(row => !hasManualOverride(cannabinoidOverrideKey(row.key).enabled)),
    ...effectiveCannabinoidRows.filter(row => [...(CANNABINOIDS as readonly string[]), 'thc'].includes(row.key)
      && hasManualOverride(cannabinoidOverrideKey(row.key).enabled)
      && manualOverrideState(manualOverrides, cannabinoidOverrideKey(row.key).enabled) === true)
      .flatMap(row => {
        const enabled = cannabinoidOverrideKey(row.key).enabled;
        const compounds = rawLabPayload?.cannabinoids || rawLabPayload?.Cannabinoids || rawLabPayload?.ingredients;
        const compound = manualCompoundValue(compounds, row.key, enabled);
        const weight = projectManualLabWeight(compoundLabWeight(compound), manualOverrides, enabled);
        return [row, ...supplementalColoradoCannabinoidRows(rawLabPayload, row.key, weight,
          { value: row.weight, uom: row.uom ?? 'mg/g' })];
      }),
  ] : effectiveCannabinoidRows;

  const getTerpene = (key: string) => {
    const overrideKey = terpeneOverrideKey(key);
    const formState = manualOverrideState(form, overrideKey.enabled);
    const savedState = manualOverrideState(manualOverrides, overrideKey.enabled);
    const overrideEnabled = formState === false || savedState === false ? false : formState ?? savedState;
    const fieldValue = (suffix: string) => manualCompoundFieldValue(
      hasManualCompoundField(manualOverrides, overrideKey.enabled, suffix) ? manualOverrides : form,
      overrideKey.enabled, suffix);
    const overrideWeight = fieldValue('weightMgG');
    const overridePercent = fieldValue('percent');

    if (overrideEnabled === false) {
      return { key, override: false, weight: undefined, percent: undefined, resultText: undefined };
    }

    if (approved || hasManualOverride(overrideKey.enabled)) {
      if (manualOverrideState(manualOverrides, overrideKey.enabled) !== true) return { key, override: false, weight: undefined, percent: undefined, resultText: undefined };
      const compound = manualCompoundValue(manualCompoundSource(rawLabPayload?.terpenes || rawLabPayload?.Terpenes, terpeneFallbackIngredients, overrideKey.enabled), key, overrideKey.enabled);
      // Project the same independently authored physical fields as Save before
      // selecting the source unit's dimension; a zero/clear cannot borrow a sibling.
      const editedWeight = { ...compoundLabWeight(compound) };
      for (const [basis, suffix] of [['amount', 'weightMgG'], ['srv', 'weightSrv'], ['pkg', 'weightPkg']]) {
        if (hasManualCompoundField(manualOverrides, overrideKey.enabled, suffix)) editedWeight[basis] = fieldValue(suffix);
      }
      const selected = selectManualLabWeight(editedWeight, manualOverrides, overrideKey.enabled);
      const percent = hasManualCompoundField(manualOverrides, overrideKey.enabled, 'percent') ? parseLabMeasurement(overridePercent) : selectLabPercent(compound);
      return { key, override: true, weight: selected.value, percent,
        resultText: percent !== undefined ? formatPercent(percent) : selected.value !== undefined ? `${selected.value} ${selected.uom}` : undefined };
    }

    const weight = overrideEnabled === true ? (overrideWeight ?? lab.terpenes[key]?.weightMgG) : lab.terpenes[key]?.weightMgG;
    const percent = overrideEnabled === true ? (overridePercent ?? lab.terpenes[key]?.percent) : lab.terpenes[key]?.percent;

    return {
      key,
      override: overrideEnabled === true,
      weight: parseLabMeasurement(weight),
      percent: parseLabMeasurement(percent),
      resultText: approved && parseLabMeasurement(percent) !== undefined ? formatPercent(parseLabMeasurement(percent)) : undefined,
    };
  }

  const pinnedTerpeneKeys = new Set(['totalTerpenes']);
  const otherTerpeneKeys = new Set(['otherTerpenes']);
  const greekGroup = (k: string) => {
    const label = TERPENE_LABELS[k] || k;
    if (pinnedTerpeneKeys.has(k)) return -1;
    if (label.startsWith('α')) return 0;
    if (label.startsWith('β')) return 1;
    if (label.startsWith('γ')) return 2;
    if (otherTerpeneKeys.has(k)) return 99;
    return 3;
  };
  const sortedTerpenes = (TERPENES as readonly string[]).slice().sort((a, b) => {
    const ga = greekGroup(a), gb = greekGroup(b);
    if (ga !== gb) return ga - gb;
    return (TERPENE_LABELS[a] || a).localeCompare(TERPENE_LABELS[b] || b);
  });

  // A reported terpene can have independent measurements in several bases.
  // Keep each helper row's identity; a whole-terpene override still replaces them all.
  const visibleTerpenes = sortedTerpenes.flatMap<TerpenePreviewRow>((key) => {
    if (approved && !hasManualOverride(terpeneOverrideKey(key).enabled)) {
      return approvedTerpenes.filter(row => row.key === key);
    }
    const row = getTerpene(key);
    const manual = manualOverrideState(manualOverrides, terpeneOverrideKey(key).enabled) === true;
    const visible = approved || manual ? row.resultText !== undefined : row.percent !== undefined && (row.override || row.percent !== 0);
    return visible ? [{ ...row, label: `${TERPENE_LABELS[key] || key}${approved && manual ? ' (override)' : ''}` }] : [];
  });
  const terpeneResultMode = approved || visibleTerpenes.some(row => manualOverrideState(manualOverrides, terpeneOverrideKey(row.key).enabled) === true);
  const terpeneMid = Math.ceil(visibleTerpenes.length / 2);
  const leftTerpenes = visibleTerpenes.slice(0, terpeneMid);
  const rightTerpenes = visibleTerpenes.slice(terpeneMid);

  const content = (
    <div className="lab-results-preview" data-testid="package-lab-results-preview">
      {showHeader ? (
        <div className="lab-results-preview-header">
          <div style={{ fontWeight: 800, fontSize: 16 }}>Lab Results</div>
        </div>
      ) : null}

      {showCoaButton ? (
        <div style={{ margin: '10px 0 12px' }}>
          <CoaLinkBar coaFiles={Array.isArray(coaFiles) ? coaFiles : []} showRemoveCoaButton={showRemoveCoaButton} />
        </div>
      ) : null}

      {showCoaFields ? (
        <div className="lab-results-preview-fields" data-testid="package-lab-results-preview-fields">
          <div className="lab-field-row"><div className="lab-field-k">Cultivar</div><div className="lab-field-v" data-testid="package-lab-results-preview-cultivar">{getOverride(labOverrideKeys.strainName) ?? (lab.cultivar || '—')}</div></div>
          <div className="lab-field-row"><div className="lab-field-k">Batch</div><div className="lab-field-v" data-testid="package-lab-results-preview-batch">{getOverride(labOverrideKeys.batchNumber) ?? (lab.batch || '—')}</div></div>
          {/* <div className="lab-field-row"><div className="lab-field-k">Quantity</div><div className="lab-field-v">{(form?.quantity ? `${form.quantity} ${ (UNITS_OF_MEASURE_OPTIONS.find(u => u.value === form.unitOfMeasure)?.value || form.unitOfMeasure || '')}` : (lab?.uom ? `${lab?.unit?.quantity ?? ''} ${lab?.uom}` : '—'))}</div></div> */}
          <div className="lab-field-row"><div className="lab-field-k">Facility</div><div className="lab-field-v" data-testid="package-lab-results-preview-facility">{getOverride(labOverrideKeys.facility) ?? (lab.facility || (lab as any).origin || form?.facilityName || '—')}</div></div>
          <div className="lab-field-row"><div className="lab-field-k">License</div><div className="lab-field-v" data-testid="package-lab-results-preview-license">{getOverride(labOverrideKeys.license) ?? (lab.license || '—')}</div></div>
          <div className="lab-field-row"><div className="lab-field-k">Tested By</div><div className="lab-field-v" data-testid="package-lab-results-preview-tested-by">{getOverride(labOverrideKeys.testedBy) ?? (lab.testedBy || '—')}</div></div>
          <div className="lab-field-row"><div className="lab-field-k">Tested Date</div><div className="lab-field-v" data-testid="package-lab-results-preview-tested-date">{formatDate(getOverride(labOverrideKeys.testedDate) ?? lab.testedDate ?? '—')}</div></div>
          {form?.[labOverrideKeys.cultivationDate.enabled] !== undefined ? (
            <div className="lab-field-row"><div className="lab-field-k">Cultivation Date</div><div className="lab-field-v">{formatDate(getOverride(labOverrideKeys.cultivationDate) ?? '—')}</div></div>
          ) : null}
            {form?.[labOverrideKeys.harvestDate.enabled] !== undefined ? (
            <div className="lab-field-row"><div className="lab-field-k">Harvest Date</div><div className="lab-field-v">{formatDate(getOverride(labOverrideKeys.harvestDate) ?? '—')}</div></div>
          ) : null}
          {form?.[labOverrideKeys.packagedDate.enabled] !== undefined ? (
            <div className="lab-field-row"><div className="lab-field-k">Packaged Date</div><div className="lab-field-v">{formatDate(getOverride(labOverrideKeys.packagedDate) ?? '—')}</div></div>
          ) : null}
          {form?.[labOverrideKeys.expirationDate.enabled] !== undefined ? (
            <div className="lab-field-row"><div className="lab-field-k">Expiration Date</div><div className="lab-field-v">{formatDate(getOverride(labOverrideKeys.expirationDate) ?? '—')}</div></div>
          ) : null}
          <div className="lab-field-row"><div className="lab-field-k">Laboratory License</div><div className="lab-field-v" data-testid="package-lab-results-preview-laboratory-license">{getOverride(labOverrideKeys.laboratoryLicense) ?? (lab.laboratoryLicense || '—')}</div></div>
          {form?.[labOverrideKeys.useByDate.enabled] !== undefined ? (
            <div className="lab-field-row"><div className="lab-field-k">Use By Date</div><div className="lab-field-v">{formatDate(getOverride(labOverrideKeys.useByDate) ?? '—')}</div></div>
          ) : null}
          {form?.[labOverrideKeys.sellByDate.enabled] !== undefined ? (
            <div className="lab-field-row"><div className="lab-field-k">Sell By Date</div><div className="lab-field-v">{formatDate(getOverride(labOverrideKeys.sellByDate) ?? '—')}</div></div>
          ) : null}
          {lab.labTestStatus ? (
            <div className="lab-field-row"><div className="lab-field-k">Lab Test Status</div><div className="lab-field-v"><span className="status-active">{lab.labTestStatus}</span></div></div>
          ) : null}
        </div>
      ) : null}

      <div style={{ fontWeight: 800, marginTop: 14, marginBottom: 8 }}>Cannabinoids</div>

      {beforeCannabinoids ? <div style={{ marginBottom: 12 }}>{beforeCannabinoids}</div> : null}

      <div data-testid="package-lab-results-preview-cannabinoids">
        <LabResultsCannabinoidsTable
          rows={cannabinoidRows}
          displayMode={cannabinoidDisplayMode}
        />
      </div>

      <div style={{ fontWeight: 800, marginTop: 14, marginBottom: 8 }}>Terpenes</div>

      {terpenesLayout === 'twoColumn' ? (
        <div className="lp-terpenes-grid">
          <div className="lab-results-preview-table-wrapper">
            <table className="lab-results-preview-table" aria-label="Terpenes" data-testid="package-lab-results-preview-terpenes-left">
              <thead>
                <tr>
                  <th>Terpene</th>
                  <th>{terpeneResultMode ? 'Result' : 'Percent'}</th>
                </tr>
              </thead>
              <tbody>
                {leftTerpenes.map((row) => {
                  return (
                    <tr key={row.rowId ?? row.key} data-testid={`package-lab-results-preview-terpene-${row.rowId ?? row.key}`}>
                      <td>{row.label}</td>
                      <td>{terpeneResultMode ? (row.resultText ?? formatPercent(row.percent)) : formatPercent(row.percent)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="lab-results-preview-table-wrapper">
            <table className="lab-results-preview-table" aria-label="Terpenes" data-testid="package-lab-results-preview-terpenes-right">
              <thead>
                <tr>
                  <th>Terpene</th>
                  <th>{terpeneResultMode ? 'Result' : 'Percent'}</th>
                </tr>
              </thead>
              <tbody>
                {rightTerpenes.map((row) => {
                  return (
                    <tr key={row.rowId ?? row.key} data-testid={`package-lab-results-preview-terpene-${row.rowId ?? row.key}`}>
                      <td>{row.label}</td>
                      <td>{terpeneResultMode ? (row.resultText ?? formatPercent(row.percent)) : formatPercent(row.percent)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="lab-results-preview-table-wrapper">
          <table className="lab-results-preview-table" aria-label="Terpenes" data-testid="package-lab-results-preview-terpenes">
            <thead>
              <tr>
                <th>Terpene</th>
                <th>{terpeneResultMode ? 'Result' : 'Percent'}</th>
              </tr>
            </thead>
            <tbody>
              {visibleTerpenes.map((row) => {
                return (
                  <tr key={row.rowId ?? row.key} data-testid={`package-lab-results-preview-terpene-${row.rowId ?? row.key}`}>
                    <td>{row.label}</td>
                    <td>{terpeneResultMode ? (row.resultText ?? formatPercent(row.percent)) : formatPercent(row.percent)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const wrapper = (
    <>
      {showPreviewLabel ? (
        <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>
          Preview
        </div>
      ) : null}
      {content}
    </>
  );

  if (!wrapInCard) return <div>{wrapper}</div>;

  return (
    <div className="card" style={{ padding: 16 }} data-testid="package-lab-results-preview-card">
      {wrapper}
    </div>
  );
}
